import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { cities, nationalContact } from '../cities';
import { Phone, ArrowRight, Check, X } from 'lucide-react';

const RegionalPresence = () => {
  const [zipCode, setZipCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!zipCode || zipCode.length < 2) {
      return;
    }

    // Suche nach PLZ in allen Regionen
    const searchZip = parseInt(zipCode.substring(0, 5));
    
    let foundCity = null;
    for (const city of cities) {
      const ranges = city.zipCodes.split(',').map(r => r.trim());
      
      for (const range of ranges) {
        if (range.includes('-')) {
          const [start, end] = range.split('-').map(z => parseInt(z));
          if (searchZip >= start && searchZip <= end) {
            foundCity = city;
            break;
          }
        } else {
          if (parseInt(range) === searchZip) {
            foundCity = city;
            break;
          }
        }
      }
      
      if (foundCity) break;
    }

    setSearchResult({
      found: !!foundCity,
      city: foundCity,
      zipCode: zipCode
    });
  };

  const handleClearResult = () => {
    setSearchResult(null);
    setZipCode('');
  };

  const handlePhoneClick = (e) => {
    e.preventDefault();
    const confirmed = window.confirm(
      `Möchten Sie ${nationalContact.phoneDisplay} anrufen?\n\n${nationalContact.description}`
    );
    if (confirmed) {
      window.location.href = `tel:${nationalContact.phone}`;
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-[#E8F5E0]" id="regionen">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A2E] mb-8">
            Sind wir auch in Ihrer Region verfügbar?
          </h2>
          
          {/* PLZ Suchfeld */}
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="Geben Sie Ihre Postleitzahl ein..."
                className="w-full px-6 py-5 pr-32 text-lg border-2 border-[#1A3A2E]/20 rounded-xl focus:outline-none focus:border-[#1A3A2E] transition-colors"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#1A3A2E] text-white px-6 py-3 rounded-lg hover:bg-[#2D4A3E] transition-colors font-semibold"
              >
                Prüfen
              </button>
            </form>
          </div>
        </div>

        {/* Ergebnis-Modal/Box */}
        {searchResult && (
          <div className="max-w-2xl mx-auto mb-12">
            <div className={`relative rounded-2xl p-8 md:p-12 text-center shadow-2xl ${
              searchResult.found 
                ? 'bg-gradient-to-br from-[#1A3A2E] to-[#2D4A3E] text-white' 
                : 'bg-white border-2 border-[#1A3A2E]/20'
            }`}>
              {/* Close Button */}
              <button
                onClick={handleClearResult}
                className={`absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors ${
                  searchResult.found ? 'text-white' : 'text-[#1A3A2E]'
                }`}
              >
                <X className="w-5 h-5" />
              </button>

              {searchResult.found ? (
                <>
                  {/* Success - Region gefunden */}
                  <div className="flex justify-center mb-6">
                    <div className="bg-white rounded-full p-4">
                      <Check className="w-12 h-12 text-[#1A3A2E]" />
                    </div>
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl font-bold mb-4">
                    Ja, wir sind auch in Ihrer Region verfügbar!
                  </h3>
                  
                  <div className="bg-white/10 rounded-lg px-6 py-4 mb-6 inline-block">
                    <p className="text-xl">
                      <span className="font-bold">{searchResult.city.name}</span>
                      <span className="mx-2">•</span>
                      <span className="text-[#E8F5E0]/90">PLZ: {searchResult.zipCode}</span>
                    </p>
                  </div>

                  <p className="text-xl text-[#E8F5E0]/90 mb-8">
                    Unsere Dienstleistungen sind in Ihrer Region verfügbar.
                  </p>

                  <div className="border-t border-white/20 pt-6">
                    <p className="text-lg mb-4">Kontaktieren Sie uns unter:</p>
                    <button
                      onClick={handlePhoneClick}
                      className="inline-flex items-center gap-3 bg-white text-[#1A3A2E] px-8 py-4 rounded-xl font-bold text-xl hover:bg-[#E8F5E0] transition-colors"
                    >
                      <Phone className="w-6 h-6" />
                      {nationalContact.phoneDisplay}
                    </button>
                    <p className="text-sm text-[#E8F5E0]/70 mt-3">
                      {nationalContact.description}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* Nicht gefunden */}
                  <h3 className="text-3xl font-bold text-[#1A3A2E] mb-4">
                    PLZ: {searchResult.zipCode}
                  </h3>
                  <p className="text-xl text-[#1A3A2E]/70 mb-6">
                    Für diese Postleitzahl haben wir noch keinen direkten Partner vor Ort.
                  </p>
                  <p className="text-lg text-[#1A3A2E]/80 mb-6">
                    Kein Problem! Kontaktieren Sie uns dennoch – wir finden eine Lösung für Sie.
                  </p>
                  <button
                    onClick={handlePhoneClick}
                    className="inline-flex items-center gap-2 bg-[#1A3A2E] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#2D4A3E] transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    {nationalContact.phoneDisplay}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* CTA Bereich */}
        <div className="mt-12 bg-[#1A3A2E] text-white rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-3xl font-bold mb-4">
            Bereit für Ihre Wärmepumpe?
          </h3>
          <p className="text-xl text-[#E8F5E0]/90 mb-8 max-w-2xl mx-auto">
            Wir sind bundesweit über unsere kostenfreie Hotline erreichbar und 
            beraten Sie gerne zu Ihrem individuellen Projekt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${nationalContact.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1A3A2E] px-8 py-4 rounded-lg font-bold hover:bg-[#E8F5E0] transition-colors"
            >
              <Phone className="w-5 h-5" />
              {nationalContact.phoneDisplay}
            </a>
            <Link
              to="/waermepumpe"
              className="inline-flex items-center justify-center gap-2 bg-[#E8F5E0] text-[#1A3A2E] px-8 py-4 rounded-lg font-bold hover:bg-white transition-colors"
            >
              Jetzt Angebot anfordern
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegionalPresence;
